import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getServices } from '../api/api';

export const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  
  const categories = ['All', 'Full Service', 'Interior Focus', 'Exterior Focus', 'Maintenance', 'Ceramic Coating'];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing Services Redlands, CA | Precision Detailing IE';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in Redlands and the Inland Empire. Interior detailing, paint enhancement, ceramic coating, and more. Serving Redlands, Riverside, San Bernardino, and surrounding areas.');
    }
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const filteredServices = filter === 'All' 
    ? services 
    : services.filter(service => service.category === filter);

  // Custom service order matching actual API service names
  const serviceOrder = {
    'Mini Detail': 1,
    'Essential Detail': 2,
    'Signature Detail Package': 3,
    'Ceramic Coating': 4,
    'Clay and Seal Package': 5,
    'Interior Reset Package': 6,
    'Paint Enhancement & Correction': 7,
    'Headlight Restoration': 8,
    'Maintenance Package': 9
  };

  const sortedServices = [...filteredServices].sort((a, b) => {
    const orderA = serviceOrder[a.name] || 999;
    const orderB = serviceOrder[b.name] || 999;
    return orderA - orderB;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading services...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Mobile Car Detailing Services in Redlands, CA</h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Professional mobile auto detailing throughout Redlands and the Inland Empire, from routine maintenance and interior detailing to paint enhancement, paint correction, ceramic coatings, and specialty services.
            </p>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mt-12">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant={filter === category ? "default" : "outline"}
                className={filter === category ? "cta-button" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-1 gap-8 max-w-5xl mx-auto">
            {sortedServices.map((service, index) => (
              <Card key={service.id} className="service-card overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Image(s) */}
                  <div className={`h-full min-h-[300px] md:min-h-[400px] overflow-hidden ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    {service.images ? (
                      // Multiple images layout
                      <div className="grid grid-rows-2 h-full gap-1">
                        {service.images.map((img, idx) => (
                          <div key={idx} className="overflow-hidden h-full">
                            <img 
                              src={img} 
                              alt={`${service.name} ${idx + 1}`}
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Single image layout
                      <img 
                        src={service.image} 
                        alt={service.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    )}
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-8 md:p-10 flex flex-col justify-between">
                    <div>
                      <div className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full mb-4">
                        {service.category}
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-4">{service.name}</h2>
                      <p className="text-gray-600 mb-6 text-lg leading-relaxed">{service.description}</p>
                      
                      {/* Features */}
                      <div className="mb-8">
                        <h3 className="font-semibold mb-4 text-xl">What's Included:</h3>
                        <ul className="space-y-3">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-gray-700">
                              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Pricing */}
                      {service.pricing ? (
                        <div className="mb-6 bg-gray-50 p-6 rounded-xl">
                          <h3 className="font-semibold mb-4 text-xl">Pricing:</h3>
                          {service.pricing.flat_rate ? (
                            // Flat rate pricing (e.g., Headlight Restoration)
                            <div className="space-y-2">
                              <div className="flex justify-center items-center">
                                <span className="font-bold text-4xl">${service.pricing.flat_rate}</span>
                              </div>
                              {service.pricing.note && (
                                <p className="text-center text-gray-600 mt-2">{service.pricing.note}</p>
                              )}
                            </div>
                          ) : (
                            // Tiered pricing (e.g., Ceramic Coating)
                            <div className="space-y-2 text-lg">
                              {service.pricing.sedan && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">Sedan</span>
                                  <span className="font-bold text-2xl">${service.pricing.sedan}</span>
                                </div>
                              )}
                              {service.pricing.midsize && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">Midsize SUV/Truck</span>
                                  <span className="font-bold text-2xl">${service.pricing.midsize}</span>
                                </div>
                              )}
                              {service.pricing.xl && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">XL SUV/Truck</span>
                                  <span className="font-bold text-2xl">${service.pricing.xl}</span>
                                </div>
                              )}
                              {service.pricing.note && (
                                <p className="text-sm text-gray-500 mt-4 italic">* {service.pricing.note}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ) : service.contact_for_pricing ? (
                        <div className="mb-6 bg-gray-50 p-6 rounded-xl border-2 border-gray-200 text-center">
                          <h3 className="font-semibold mb-2 text-2xl">Contact For Pricing</h3>
                          <p className="text-gray-600">Custom pricing based on vehicle condition and requirements</p>
                        </div>
                      ) : service.unlock_message ? (
                        <div className="mb-6 bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-200">
                          <h3 className="font-semibold mb-3 text-xl flex items-center gap-2">
                            <span className="text-2xl">🔒</span> Unlock This Package
                          </h3>
                          <p className="text-gray-700 leading-relaxed">{service.unlock_message}</p>
                        </div>
                      ) : null}
                      
                      <div className="text-sm text-gray-500 mb-6">
                        <span className="font-semibold">Duration:</span> {service.duration}
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <Button asChild size="lg" className="cta-button w-full text-lg">
                      <Link to="/booking">
                        Book This Service <ArrowRight className="ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
          
          {sortedServices.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">No services found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Transform Your Vehicle?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Book your appointment today and experience professional mobile detailing at your location
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
              <Link to="/booking">
                Book Now <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300">
              <a href="tel:(909) 654-4475">
                Call (909) 654-4475
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
